import { Vue } from 'vue-property-decorator'
import axios from '@/services/base.service'
import { AxiosResponse } from 'axios'
import { Meta, StatusFilter } from '@/interfaces/global/paginate.interface'
import { ErrorMessages } from '@/helpers/constants'
import { getFirstErrorMessage } from '@/utils/validation/validation.utils'
import { VenueAdmin } from '@/interfaces/admin/venues/venue-admin.interface'

const resource = 'v2/venues'

export const getVenue = async (venueId: number): Promise<VenueAdmin> => {
  try {
    const response: AxiosResponse<VenueAdmin> = await axios.get(`${resource}/${venueId}`)
    return response.data
  } catch (error) {
    const message = error.response?.data?.message || ErrorMessages.COMMON
    Vue.$toast.error(message)
    throw error
  }
}

export const getAllVenues = async (
  page: number = 1,
  status: string = StatusFilter.ALL,
  filter: string = ''
): Promise<{ data: VenueAdmin[]; meta: Meta }> => {
  try {
    const params: any = { page }
    if (status !== StatusFilter.ALL) params.status = status === StatusFilter.ACTIVE ? 1 : 0
    if (filter) params.q = filter

    const { data: payload } = await axios.get(`v2/venues`, { params }) // paginator Laravel

    const meta: Meta = {
      total: payload.total,
      per_page: payload.per_page,
      current_page: payload.current_page,
      last_page: payload.last_page,
      from: payload.from,
      to: payload.to,
      path: payload.path,
    }

    return { data: payload.data as VenueAdmin[], meta }
  } catch (error) {
    const message = error.response?.data?.message || ErrorMessages.COMMON
    Vue.$toast.error(message)
    throw error
  }
}

export const getActiveVenues = async (): Promise<VenueAdmin[]> => {
  try {
    const response: AxiosResponse<VenueAdmin[]> = await axios.get(`${resource}/active`)
    return response.data
  } catch (error) {
    const message = error.response?.data?.message || ErrorMessages.COMMON
    Vue.$toast.error(message)
    throw error
  }
}

export const toggleActiveVenue = async (venueId: number): Promise<VenueAdmin> => {
  try {
    const response: AxiosResponse<VenueAdmin> = await axios.patch(`${resource}/${venueId}/toggle`)
    return response.data
  } catch (error) {
    const message = error.response?.data?.message || ErrorMessages.COMMON
    Vue.$toast.error(message)
    throw error
  }
}

export const updateVenue = async (venue: VenueAdmin): Promise<VenueAdmin> => {
  try {
    const response: AxiosResponse<VenueAdmin> = await axios.put(`${resource}/${venue.id}`, venue)
    return response.data
  } catch (error) {
    const message = error.response?.data?.message || ErrorMessages.COMMON
    Vue.$toast.error(message)
    throw error
  }
}

export const createVenue = async (venue: VenueAdmin): Promise<VenueAdmin> => {
  try {
    const response: AxiosResponse<VenueAdmin> = await axios.post(`${resource}`, venue)
    return response.data
  } catch (error) {
    const status = error.response?.status
    let message: string
    if (status === 422) {
      message = getFirstErrorMessage(error.response?.data.errors)
    } else {
      message = error.response?.data?.message || ErrorMessages.COMMON
    }
    Vue.$toast.error(message)
    throw error
  }
}

export const deleteVenue = async (venueId: number): Promise<void> => {
  try {
    await axios.delete(`${resource}/${venueId}`) // 204 sin body
  } catch (error) {
    const message = error.response?.data?.message || ErrorMessages.COMMON
    Vue.$toast.error(message)
    throw error
  }
}

export const restoreVenue = async (venueId: number): Promise<VenueAdmin> => {
  try {
    const response: AxiosResponse<VenueAdmin> = await axios.post(`${resource}/${venueId}/restore`)
    return response.data
  } catch (error) {
    const message = error.response?.data?.message || ErrorMessages.COMMON
    Vue.$toast.error(message)
    throw error
  }
}
